import { FC, useEffect, useState} from "react";
import Table from "./Table";
import {
  DOORS_INDEXES,
  answer,
  choose,
  defaultAnswer,
  generateSecret,
  randomDoor,
  secretType,
  wrongOpenDoor as wrongDoorToReveal,
} from "./ThreeDoorSolver";
import Summary from "./TableSummary";
import Tabs from "./Components/Tabs/Tabs";
import Tab from "./Components/Tabs/Tab";

const ManualSolve = () => {
  const [history, setHistory] = useState<choose[]>([]);
  const [answer, setAnswer] = useState<answer>(defaultAnswer);
  const [secret, setSecret] = useState<secretType>(
    generateSecret(randomDoor()),
  );
  const [finalOpenDoor, setFinalOpenDoor] = useState(-1);
  const [wrongRevealedDoor, setWrongRevealedDoor] = useState(-1);

  useEffect(() => {
    if (finalOpenDoor >= 0) {
      setHistory((h) => [
        ...h,
        {
          id: h.length,
          answer: answer,
          secret: secret,
          dateWhenComplete: new Date(),
        },
      ]);

      setTimeout(() => {
        const newRandomRight = randomDoor();
        const newSecret = generateSecret(newRandomRight);
        setWrongRevealedDoor(-1);
        setFinalOpenDoor(-1);
        setAnswer(defaultAnswer);
        setSecret(newSecret);
      }, 800);
    }
  }, [finalOpenDoor, answer, secret, wrongRevealedDoor]);

  const isStageTwo = answer.select !== -1;

  return (
    <div className="flex flex-col items-center my-16">
      <div className="text-8xl sm:text-mega-xl text-center mb-8 sm:my-0">
        {DOORS_INDEXES.map((i) => {
          const isRevealedWrong = wrongRevealedDoor === i;
          const isOpenedByUser = finalOpenDoor === i;
          const isOpened = isOpenedByUser || isRevealedWrong;

          const isBlocked = isOpened || answer.openWrong === i;
          const isWrong = secret[i] === "wrong";
          const isSelected = answer.select === i;

          const status: DoorStatus = isOpened
            ? isWrong
              ? "wrong"
              : "right"
            : isSelected
              ? "selected"
              : "closed";

          return (
            <Door
              key={`door-${i}`}
              status={status}
              blocked={isBlocked}
              stageTwo={isStageTwo}
              onClick={() => {
                const wd = wrongDoorToReveal(secret, i);
                setTimeout(() => setWrongRevealedDoor(wd), 300);
                setAnswer((prev) => ({
                  ...prev,
                  select: i,
                  openWrong: wd,
                }));
              }}
              onSecondClick={() => {
                if (finalOpenDoor === -1 && answer.openWrong !== i) {
                  setFinalOpenDoor(i);
                  setAnswer((prev) => ({
                    ...prev,
                    change: prev.select !== i,
                  }));
                }
              }}
            />
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-5 text-center mb-12 sm:mb-20">
        <button
          disabled={answer.select === -1 || finalOpenDoor !== -1}
          className="button-secondary-medium"
          onClick={() => {
            setAnswer((prev) => ({
              ...prev,
              change: false,
            }));
            setFinalOpenDoor(answer.select);
          }}
        >
          stay
        </button>
        <button
          disabled={answer.select === -1 || finalOpenDoor !== -1}
          className="button-secondary-medium"
          onClick={() => {
            setAnswer((prev) => ({
              ...prev,
              change: true,
            }));
            setFinalOpenDoor(
              DOORS_INDEXES.filter(
                (i) => i !== answer.select && i !== wrongRevealedDoor,
              )[0],
            );
          }}
        >
          change
        </button>
      </div>

      <Tabs
        className="
          flex w-full justify-stretch items-start pb-52 text-black
          xl:space-x-10
        "
        buttonsSectionProps={{
          className: "xl:hidden",
        }}
      >
        <Tab label="History" className="w-full xl:block">
          <Table
            choses={history}
            name={"History"}
            className="md:mx-auto xl:mx-0"
            showTime
            reverse
          />
        </Tab>
        <Tab label="Summary" className="w-full xl:block">
          <Summary choses={history} />
        </Tab>
      </Tabs>
    </div>
  );
};
export default ManualSolve;

type DoorStatus = "closed" | "selected" | "wrong" | "right";

type DoorProps = {
  status: DoorStatus;
  blocked: boolean;
  stageTwo: boolean;
  onClick: () => void;
  onSecondClick: () => void;
};

const Door: FC<DoorProps> = ({
  status,
  blocked,
  stageTwo,
  onClick,
  onSecondClick,
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => (stageTwo ? onSecondClick() : onClick())}
      className={`cursor-pointer select-none font-symbol ${color(status, hovered, blocked)}`}
    >
      {text(status)}
    </span>
  );
};

const text = (status: DoorStatus) => {
  switch (status) {
    case "wrong":
      return "▩";
    case "right":
      return "■";
    case "selected":
      return "▣";
    default:
      return "□";
  }
};

const color = (status: DoorStatus, hovered: boolean, blocked: boolean) => {
  if (hovered && !blocked) return "text-green";
  if (status === "wrong") return "text-red";
  if (status === "right") return "text-green";
  return "text-black";
};